import { Button, Card, Drawer, Empty, Form, Input, InputNumber, Radio, Tag, notification } from "antd";
import { get } from "lodash";
import { useState } from "react";
import { BiPlus, BiTrash } from "react-icons/bi";
import { useMutation, useQuery } from "shared/hooks/useApi";
import graphql from "utils/api/graphql";
import cuid from "cuid";

const SelectCoverages = ({ values, setFieldValue, data }) => {
  const [search, setSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [configDrawerIndex, setConfigDrawerIndex] = useState(null);
  const [form] = Form.useForm();
  const isComposable = get(values, "pricingMode") === "COMPOSABLE";
  const planId = data.id;

  const { data: categoriesData, loading } = useQuery(
    graphql.queries.COVERAGE_CATEGORIES,
    { variables: {}, fetchPolicy: "network-only" },
  );

  const [addCoverage] = useMutation(
    graphql.mutations.CREATE_INSURANCE_PLAN_COVERAGE,
  );

  const [deleteCoverage] = useMutation(
    graphql.mutations.DELETE_INSURANCE_PLAN_COVERAGE,
  );

  const [updateCoverage] = useMutation(
    graphql.mutations.UPDATE_INSURANCE_PLAN_COVERAGE,
  );

  const [updateCategoryMutation] = useMutation(
    graphql.mutations.UPDATE_COVERAGE_CATEGORY,
  );

  const [createCategory, { loading: creating }] = useMutation(
    graphql.mutations.CREATE_COVERAGE_CATEGORY,
    {
      refetchQueries: [
        { query: graphql.queries.COVERAGE_CATEGORIES, variables: {} },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => {
        form.resetFields();
        setDrawerOpen(false);
      },
    },
  );

  const categories = get(categoriesData, "coverageCategories.data") || [];
  const selectedCoverages = get(values, "insurancePlanCoverages") || [];
  const selectedIds = selectedCoverages.map((c) =>
    get(c, "coverageCategory.id"),
  );

  const availableCategories = categories.filter(
    (cat) =>
      !selectedIds.includes(cat.id) &&
      (cat.label || "").toLowerCase().includes(search.toLowerCase()),
  );

  const handleAddCoverage = async (category) => {
    const id = cuid();
    const needsId = cuid();
    const newCoverage = {
      id,
      pricingMode: "PACKAGED",
      coverageCategory: category,
      needs: { id: needsId, level: 1 },
      pricer: null,
      coverageMappings: [],
    };

    try {
      await addCoverage({
        variables: {
          data: {
            id,
            pricingMode: "PACKAGED",
            insurancePlan: { id: planId },
            coverageCategory: { id: category.id },
            needs: { id: needsId, level: 1 },
          },
        },
      });
      setFieldValue("insurancePlanCoverages", [
        ...selectedCoverages,
        newCoverage,
      ]);
      notification.success({ message: `Ajouté : ${category.label}` });
    } catch (err) {
      notification.error({ message: "Échec de l'ajout de la garantie" });
    }
  };

  const handleRemoveCoverage = async (index) => {
    const coverage = selectedCoverages[index];
    try {
      await deleteCoverage({
        variables: { where: { id: coverage.id } },
      });
      setFieldValue(
        "insurancePlanCoverages",
        selectedCoverages.filter((_, i) => i !== index),
      );
      notification.success({ message: "Garantie supprimée" });
    } catch (err) {
      notification.error({ message: "Échec de la suppression" });
    }
  };

  const handleCreateCategory = () => {
    form.validateFields().then(async (formValues) => {
      const categoryId = cuid();
      const coverageId = cuid();
      const needsId = cuid();

      try {
        await createCategory({
          variables: {
            data: {
              id: categoryId,
              value: formValues.value,
              label: formValues.label,
              description: formValues.description,
              order: parseInt(formValues.order, 10) || 1,
              weight: parseInt(formValues.weight, 10) || 1,
            },
          },
        });

        await addCoverage({
          variables: {
            data: {
              id: coverageId,
              pricingMode: formValues.pricingMode || "PACKAGED",
              carenceDays: parseInt(formValues.carenceDays, 10) || null,
              franchiseAmount: parseFloat(formValues.franchiseAmount) || null,
              insurancePlan: { id: planId },
              coverageCategory: { id: categoryId },
              needs: { id: needsId, level: 1 },
            },
          },
        });

        setFieldValue("insurancePlanCoverages", [
          ...selectedCoverages,
          {
            id: coverageId,
            pricingMode: formValues.pricingMode || "PACKAGED",
            carenceDays: parseInt(formValues.carenceDays, 10) || null,
            franchiseAmount: parseFloat(formValues.franchiseAmount) || null,
            coverageCategory: {
              id: categoryId,
              value: formValues.value,
              label: formValues.label,
              icon: null,
              order: parseInt(formValues.order, 10) || 1,
              weight: parseInt(formValues.weight, 10) || 1,
            },
            needs: { id: needsId, level: 1 },
            pricer: null,
            coverageMappings: [],
          },
        ]);

        form.resetFields();
        setDrawerOpen(false);
        notification.success({ message: `Créé et ajouté : ${formValues.label}` });
      } catch (err) {
        notification.error({ message: "Échec de la création" });
      }
    });
  };

  return (
    <div className="configure-step">
      <div className="d-flex" style={{ gap: "24px", alignItems: "stretch" }}>
        <Card
          title="Garanties disponibles"
          style={{ flex: 1 }}
          extra={
            <div className="d-flex" style={{ gap: "8px" }}>
              <Input.Search
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                allowClear
                style={{ width: 200 }}
              />
              <Button
                type="primary"
                icon={<BiPlus size={16} />}
                onClick={() => setDrawerOpen(true)}
              >
                Nouveau
              </Button>
            </div>
          }
        >
          {loading ? (
            <div style={{ textAlign: "center", padding: 24 }}>Chargement...</div>
          ) : availableCategories.length === 0 ? (
            <Empty
              description={
                categories.length === 0
                  ? "Aucune garantie disponible. Cliquez sur 'Nouveau' pour en créer."
                  : "Toutes les garanties sont sélectionnées"
              }
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "12px",
              }}
            >
              {availableCategories.map((category) => (
                <Card
                  key={category.id}
                  size="small"
                  hoverable
                  style={{ borderRadius: 8, cursor: "pointer" }}
                  onClick={() => handleAddCoverage(category)}
                >
                  <div
                    className="d-flex"
                    style={{ gap: "12px", alignItems: "center" }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 4,
                        background: "#e6f4ff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        color: "#0e93e5",
                      }}
                    >
                      {(category.label || category.value || "?")[0].toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600 }}>
                        {category.label || category.value}
                      </div>
                      {category.description && (
                        <div style={{ fontSize: 12, color: "#8A92A5" }}>
                          {category.description}
                        </div>
                      )}
                    </div>
                    <BiPlus size={20} style={{ color: "#0e93e5" }} />
                  </div>
                </Card>
              ))}
            </div>
          )}
        </Card>

        <Card
          title={
            <span>
              Garanties sélectionnées{" "}
              <Tag color="blue">{selectedCoverages.length}</Tag>
            </span>
          }
          style={{ flex: 1 }}
        >
          {selectedCoverages.length === 0 ? (
            <Empty description="Cliquez sur une garantie à gauche pour l'ajouter." />
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {selectedCoverages.map((coverage, index) => {
                const cat = get(coverage, "coverageCategory", {});
                return (
                  <Card
                    key={coverage.id}
                    size="small"
                    style={{ borderRadius: 8 }}
                  >
                    <div
                      className="d-flex justify--between"
                      style={{ alignItems: "center" }}
                    >
                      <div
                        className="d-flex"
                        style={{ gap: "12px", alignItems: "center" }}
                      >
                        <div
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 4,
                            background: "#e6f4ff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontWeight: "bold",
                            color: "#0e93e5",
                          }}
                        >
                          {(cat.label || cat.value || "?")[0].toUpperCase()}
                        </div>
                        <div style={{ fontWeight: 600 }}>
                          {cat.label || cat.value}
                        </div>
                      </div>
                      <Button
                        type="text"
                        danger
                        size="small"
                        icon={<BiTrash size={16} />}
                        onClick={() => handleRemoveCoverage(index)}
                      />
                    </div>
                    <div className="m-top-8">
                      <Button
                        type="link"
                        size="small"
                        onClick={() => setConfigDrawerIndex(index)}
                        style={{ padding: 0 }}
                      >
                        Configurer
                        {get(coverage, "carenceDays") ? ` · Carence: ${get(coverage, "carenceDays")}j` : ""}
                        {get(coverage, "franchiseAmount") ? ` · Franchise: ${get(coverage, "franchiseAmount")}€` : ""}
                        {` · ${get(coverage, "pricingMode", "PACKAGED")}`}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      <Drawer
        title={
          configDrawerIndex !== null
            ? `Configurer : ${get(selectedCoverages, `${configDrawerIndex}.coverageCategory.label`, "")}`
            : ""
        }
        open={configDrawerIndex !== null}
        onClose={() => setConfigDrawerIndex(null)}
        width={400}
      >
        {configDrawerIndex !== null && (() => {
          const coverage = selectedCoverages[configDrawerIndex];
          const cat = get(coverage, "coverageCategory", {});
          const idx = configDrawerIndex;

          const updateCoverageField = async (field, value) => {
            const updated = [...selectedCoverages];
            updated[idx] = { ...updated[idx], [field]: value };
            setFieldValue("insurancePlanCoverages", updated);
          };

          const saveCoverageField = async (field) => {
            try {
              await updateCoverage({
                variables: {
                  where: { id: coverage.id },
                  data: { [field]: get(coverage, field) },
                },
              });
            } catch (err) {
              notification.error({ message: "Échec de la mise à jour" });
            }
          };

          const updateCatField = (field, value) => {
            const updated = [...selectedCoverages];
            updated[idx] = {
              ...updated[idx],
              coverageCategory: { ...cat, [field]: value },
            };
            setFieldValue("insurancePlanCoverages", updated);
          };

          const saveCatField = async (field) => {
            try {
              await updateCategoryMutation({
                variables: {
                  where: { id: cat.id },
                  data: { [field]: cat[field] },
                },
              });
            } catch (err) {
              notification.error({ message: "Échec de la mise à jour de la catégorie" });
            }
          };

          return (
            <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
              <h4 style={{ marginBottom: 0 }}>Catégorie</h4>
              <div>
                <label className="input__label">Libellé</label>
                <Input
                  value={cat.label}
                  onChange={(e) => updateCatField("label", e.target.value)}
                  onBlur={() => saveCatField("label")}
                  placeholder="e.g. Dental"
                />
              </div>
              <div>
                <label className="input__label">Code</label>
                <Input
                  value={cat.value}
                  onChange={(e) => updateCatField("value", e.target.value)}
                  onBlur={() => saveCatField("value")}
                  placeholder="e.g. DENTAL"
                />
              </div>
              <div>
                <label className="input__label">Description</label>
                <Input.TextArea
                  value={cat.description || ""}
                  onChange={(e) => updateCatField("description", e.target.value)}
                  onBlur={() => saveCatField("description")}
                  placeholder="Describe this coverage"
                  rows={2}
                />
              </div>
              <div className="d-flex" style={{ gap: "16px" }}>
                <div style={{ flex: 1 }}>
                  <label className="input__label">Ordre</label>
                  <InputNumber
                    value={cat.order}
                    min={0}
                    onChange={(val) => updateCatField("order", val)}
                    onBlur={() => saveCatField("order")}
                    style={{ width: "100%" }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="input__label">Poids</label>
                  <InputNumber
                    value={cat.weight}
                    min={0}
                    onChange={(val) => updateCatField("weight", val)}
                    onBlur={() => saveCatField("weight")}
                    style={{ width: "100%" }}
                  />
                </div>
              </div>

              <h4 style={{ marginBottom: 0, marginTop: 8 }}>Paramètres de la garantie</h4>
              <div className="d-flex" style={{ gap: "16px" }}>
                <div style={{ flex: 1 }}>
                  <label className="input__label">Carence (jours)</label>
                  <InputNumber
                    min={0}
                    value={get(coverage, "carenceDays")}
                    onChange={(val) => updateCoverageField("carenceDays", val)}
                    onBlur={() => saveCoverageField("carenceDays")}
                    style={{ width: "100%" }}
                    placeholder="0"
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label className="input__label">Franchise (€)</label>
                  <InputNumber
                    min={0}
                    step={0.01}
                    value={get(coverage, "franchiseAmount")}
                    onChange={(val) => updateCoverageField("franchiseAmount", val)}
                    onBlur={() => saveCoverageField("franchiseAmount")}
                    style={{ width: "100%" }}
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <label className="input__label">Mode de tarification</label>
                <Radio.Group
                  value={get(coverage, "pricingMode", "PACKAGED")}
                  onChange={async (e) => {
                    const newMode = e.target.value;
                    try {
                      await updateCoverage({
                        variables: {
                          where: { id: coverage.id },
                          data: { pricingMode: newMode },
                        },
                      });
                      updateCoverageField("pricingMode", newMode);
                    } catch (err) {
                      notification.error({ message: "Échec de la mise à jour" });
                    }
                  }}
                >
                  <Radio.Button value="PACKAGED">Forfaitaire</Radio.Button>
                  <Radio.Button value="COMPOSABLE">Par prestation</Radio.Button>
                </Radio.Group>
              </div>
            </div>
          );
        })()}
      </Drawer>

      <Drawer
        title="Nouvelle catégorie de garantie"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={400}
        extra={
          <Button
            type="primary"
            onClick={handleCreateCategory}
            loading={creating}
          >
            Créer
          </Button>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="label"
            label="Libellé"
            rules={[{ required: true, message: "Label is required" }]}
          >
            <Input placeholder="e.g. Dental, Optical..." />
          </Form.Item>
          <Form.Item
            name="value"
            label="Code"

            rules={[{ required: true, message: "Code is required" }]}
          >
            <Input placeholder="e.g. DENTAL, OPTICAL..." />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea
              placeholder="Describe this coverage category"
              rows={3}
            />
          </Form.Item>
          <div className="d-flex" style={{ gap: "16px" }}>
            <Form.Item name="order" label="Ordre" style={{ flex: 1 }}>
              <Input type="number" placeholder="1" />
            </Form.Item>
            <Form.Item name="weight" label="Poids" style={{ flex: 1 }}>
              <Input type="number" placeholder="1" />
            </Form.Item>
          </div>
          <div className="d-flex" style={{ gap: "16px" }}>
            <Form.Item name="carenceDays" label="Carence (jours)" style={{ flex: 1 }}>
              <InputNumber min={0} style={{ width: "100%" }} placeholder="0" />
            </Form.Item>
            <Form.Item name="franchiseAmount" label="Franchise (€)" style={{ flex: 1 }}>
              <InputNumber min={0} step={0.01} style={{ width: "100%" }} placeholder="0" />
            </Form.Item>
          </div>
          <Form.Item name="pricingMode" label="Mode de tarification" initialValue="PACKAGED">
            <Radio.Group>
              <Radio.Button value="PACKAGED">Forfaitaire</Radio.Button>
              <Radio.Button value="COMPOSABLE">Par prestation</Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  );
};

export default SelectCoverages;
